import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaBuilding, FaCodeBranch, FaInfoCircle, FaPhone } from "react-icons/fa";
import { toast } from "react-toastify";
import { useGoogleMaps } from "../GoogleMapsContext";
import { useCountry } from "../context/CountryContext.js";
import { getMarket } from "../config/markets.js";
import VendorAddressAutocomplete from "./VendorAddressAutocomplete";
import "./BrandOutletFormModal.css";
// What the radius and overage limit do at checkout: views/Settings/help/vendorSettingsHelp.js.
import SettingHelp from "../views/Settings/settingsHelp";

/**
 * BrandOutletFormModal: the ONE form for a brand outlet (branch).
 *
 * Used for create (BrandManagement "Add outlet", OutletManagementBrand "Add
 * outlet") and edit (OutletManagementBrand). The server contract splits the
 * fields by who may write them:
 *
 *   Lusuku staff      name, address, latitude, longitude, deliveryZoneRadius,
 *                     deliveryOverageMaxKm, phone, email, managerName
 *   Brand owner/team  phone, email, managerName
 *
 * `canEditLocation` is the staff flag. When it is off the location block
 * renders read-only and the submitted body carries contact fields only, so a
 * brand owner never sends a key the server would reject.
 *
 * The parent owns the request: `onSubmit(body)` is awaited, and the parent
 * closes the modal and toasts on success (errors are toasted here from the
 * axios response so both pages surface them the same way).
 */

export const BRAND_OUTLET_LOCATION_FIELDS = Object.freeze([
  "name",
  "address",
  "latitude",
  "longitude",
  "deliveryZoneRadius",
  "deliveryOverageMaxKm",
]);
export const BRAND_OUTLET_CONTACT_FIELDS = Object.freeze(["phone", "email", "managerName"]);

const DEFAULT_RADIUS_KM = 5;

const emptyForm = () => ({
  name: "",
  address: "",
  latitude: null,
  longitude: null,
  deliveryZoneRadius: DEFAULT_RADIUS_KM,
  deliveryOverageMaxKm: "",
  phone: "",
  email: "",
  managerName: "",
});

const formFromOutlet = (outlet) => ({
  name: outlet.name || "",
  address: outlet.address || "",
  latitude: outlet.latitude ?? null,
  longitude: outlet.longitude ?? null,
  deliveryZoneRadius: outlet.deliveryZoneRadius ?? DEFAULT_RADIUS_KM,
  deliveryOverageMaxKm: outlet.deliveryOverageMaxKm ?? "",
  phone: outlet.phone || "",
  email: outlet.email || "",
  managerName: outlet.managerName || "",
});

const numberOrUndefined = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Turn the form state into the request body the contract accepts for this
 * caller. Exported so a test or another caller can build the same body
 * without the modal.
 */
export const buildBrandOutletBody = (form, { canEditLocation }) => {
  const body = {};
  BRAND_OUTLET_CONTACT_FIELDS.forEach((key) => {
    body[key] = String(form[key] ?? "").trim();
  });
  if (!canEditLocation) return body;

  body.name = String(form.name || "").trim();
  body.address = String(form.address || "").trim();
  body.latitude = form.latitude;
  body.longitude = form.longitude;
  body.deliveryZoneRadius = numberOrUndefined(form.deliveryZoneRadius);
  const overage = numberOrUndefined(form.deliveryOverageMaxKm);
  if (overage !== undefined) body.deliveryOverageMaxKm = overage;
  return body;
};

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <section className="bof-section">
    <div className="bof-section-header">
      {Icon && <Icon style={{ color: "#0a578b", fontSize: "0.85rem" }} />}
      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <span className="bof-section-title">{title}</span>
        {subtitle ? <span className="bof-section-sub">{subtitle}</span> : null}
      </div>
    </div>
    <div className="bof-section-body">{children}</div>
  </section>
);

const ReadOnlyField = ({ label, value }) => (
  <Form.Group>
    <Form.Label className="bof-label">{label}</Form.Label>
    <Form.Control className="bof-control" size="sm" type="text" value={value ?? ""} readOnly disabled />
  </Form.Group>
);

const BrandOutletFormModal = ({
  show,
  onHide,
  brand,
  outlet = null,
  canEditLocation = false,
  isSubmitting = false,
  onSubmit,
}) => {
  const { mapsApiLoaded } = useGoogleMaps();
  const { activeCountry } = useCountry();
  const [form, setForm] = useState(emptyForm);
  const isEdit = Boolean(outlet);
  // The Places field is restricted to the brand's own country. A brand row
  // without a country (older list projections) falls back to the market the
  // operator is viewing, which is what the brand form itself uses.
  const market = getMarket(brand?.country || activeCountry);

  useEffect(() => {
    if (!show) return;
    setForm(outlet ? formFromOutlet(outlet) : emptyForm());
  }, [show, outlet]);

  const setField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!brand?._id || !onSubmit) return;
    if (canEditLocation && (form.latitude == null || form.longitude == null)) {
      toast.error("Select the address from the suggestions so the outlet gets map coordinates.");
      return;
    }
    try {
      await onSubmit(buildBrandOutletBody(form, { canEditLocation }));
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          (isEdit ? "Failed to update outlet" : "Failed to create outlet")
      );
    }
  };

  const title = isEdit ? `Edit outlet: ${outlet.name}` : `Add outlet for ${brand?.name || "brand"}`;
  const submitLabel = isEdit ? "Save changes" : "Create outlet";
  const busyLabel = isEdit ? "Saving..." : "Creating...";

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? undefined : onHide}
      dialogClassName="bof-dialog"
      style={{ overflow: "visible" }}
      enforceFocus={false}
      backdrop="static"
    >
      <Modal.Header closeButton={!isSubmitting} className="bof-header">
        <Modal.Title>
          <FaCodeBranch style={{ marginRight: 10, fontSize: "0.9rem" }} />
          {title}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="bof-body">
          <Section
            icon={FaBuilding}
            title="Location and delivery zone"
            subtitle={canEditLocation ? "Name, pickup address, GPS coordinates and delivery radius" : undefined}
          >
            {canEditLocation ? (
              <div className="row g-2">
                <div className="col-12">
                  <Form.Group>
                    <Form.Label className="bof-label">
                      Outlet name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      className="bof-control"
                      size="sm"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={setField}
                      required
                      placeholder="e.g., Kampala Branch"
                    />
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group>
                    <Form.Label className="bof-label">
                      Address <span className="text-danger">*</span>
                    </Form.Label>
                    {/* key={market.country} remounts the autocomplete with the
                        right country restriction when the brand changes. */}
                    <VendorAddressAutocomplete
                      key={`${brand?._id || "new"}-${market.country}`}
                      market={market}
                      mapsApiLoaded={mapsApiLoaded}
                      defaultValue={form.address}
                      onSelect={({ address, latitude, longitude }) =>
                        setForm((p) => ({ ...p, address, latitude, longitude }))
                      }
                      onClear={() =>
                        setForm((p) => ({ ...p, address: "", latitude: null, longitude: null }))
                      }
                    />
                    <Form.Text className="bof-help">
                      Start typing to search within {market.name || market.country}. Select a suggestion to capture GPS coordinates.
                    </Form.Text>
                    {form.latitude != null && form.longitude != null && (
                      <Form.Text className="text-success d-block mt-1" style={{ fontSize: "0.75rem" }}>
                        Location set: {Number(form.latitude).toFixed(6)}, {Number(form.longitude).toFixed(6)}
                      </Form.Text>
                    )}
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="bof-label">
                      Delivery zone radius (km) <span className="text-danger">*</span>
                      <SettingHelp topic="vendorOutletRadius" />
                    </Form.Label>
                    <Form.Control
                      className="bof-control"
                      size="sm"
                      type="number"
                      name="deliveryZoneRadius"
                      value={form.deliveryZoneRadius}
                      onChange={setField}
                      required
                      min="1"
                      step="0.1"
                    />
                    <Form.Text className="bof-help">Customers inside this radius can order from this outlet at the normal delivery price. Not used when the brand delivers nationwide.</Form.Text>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label className="bof-label">Overage limit (km)<SettingHelp topic="vendorOutletOverage" /></Form.Label>
                    <Form.Control
                      className="bof-control"
                      size="sm"
                      type="number"
                      name="deliveryOverageMaxKm"
                      value={form.deliveryOverageMaxKm}
                      onChange={setField}
                      min="0"
                      step="0.1"
                      placeholder="Optional"
                    />
                    <Form.Text className="bof-help">
                      Extra distance beyond the radius still served at an overage fee. Blank uses the brand's
                      limit, or Lusuku's standard limit when the brand has none. Enter 0 for no overage.
                      Clearing a saved value does not reset it; enter the number you want.
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            ) : (
              <>
                <div className="bof-readonly-note">
                  <FaInfoCircle style={{ marginTop: 2, flexShrink: 0 }} />
                  <span>Only Lusuku staff can change the outlet name, address and delivery zone. Contact Lusuku support to move this outlet.</span>
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <ReadOnlyField label="Outlet name" value={form.name} />
                  </div>
                  <div className="col-md-6">
                    <ReadOnlyField label="Address" value={form.address} />
                  </div>
                  <div className="col-md-6">
                    <ReadOnlyField label={<>Delivery zone radius (km)<SettingHelp topic="vendorOutletRadius" /></>} value={form.deliveryZoneRadius} />
                  </div>
                  <div className="col-md-6">
                    <ReadOnlyField label={<>Overage limit (km)<SettingHelp topic="vendorOutletOverage" /></>} value={form.deliveryOverageMaxKm === "" ? "Brand or Lusuku standard limit" : form.deliveryOverageMaxKm} />
                  </div>
                </div>
              </>
            )}
          </Section>

          <Section icon={FaPhone} title="Outlet contact" subtitle="Who customers and riders reach at this branch">
            <div className="row g-2">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="bof-label">Phone</Form.Label>
                  <Form.Control
                    className="bof-control"
                    size="sm"
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={setField}
                    placeholder={market.dialCode ? `+${market.dialCode}700000000` : "Phone number"}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="bof-label">Email</Form.Label>
                  <Form.Control
                    className="bof-control"
                    size="sm"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={setField}
                    placeholder="branch@brand.com"
                  />
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group>
                  <Form.Label className="bof-label">Manager name</Form.Label>
                  <Form.Control
                    className="bof-control"
                    size="sm"
                    type="text"
                    name="managerName"
                    value={form.managerName}
                    onChange={setField}
                    placeholder="Branch manager"
                  />
                </Form.Group>
              </div>
            </div>
          </Section>
        </Modal.Body>
        <Modal.Footer className="bof-footer">
          <Button className="bof-btn-secondary" variant="outline-secondary" size="sm" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button className="bof-btn-primary" size="sm" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                {busyLabel}
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BrandOutletFormModal;
